import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { Item } from 'app/models/item.inferface';
import { BehaviorSubject, Observable, of as observableOf, Subject } from 'rxjs';
import { startWith, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ISuggestService } from './model/ISuggestService';
import { MatAutocomplete } from '@angular/material/autocomplete';

interface ExtendendItem extends Item {
  descriptionPropertyName: string
}

@Component({
  selector: 'app-suggest-input',
  templateUrl: './suggest-input.component.html',
  styleUrls: ['./suggest-input.component.scss']
})
export class SuggestInputComponent<T extends Item> implements OnInit {
  loading$: Subject<boolean>;

  @Input() formGroup: UntypedFormGroup;
  @Input() controlName: string;
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() descriptionPropertyName: string = 'description';

  @Input() itemDescription: string;
  @Output() itemChange: EventEmitter<T> = new EventEmitter();

  _initialValue$: BehaviorSubject<T>;
  @Input() set initialValue(value: T) {
    this._initialValue$.next(value);
  }

  @Input() detailMode: boolean = false;

  @Input() suggestService: ISuggestService<T> = null;


  // @ViewChild('inputField') inputField: MatInput;
  @ViewChild('auto') inputField: MatAutocomplete;

  private searchPatternMinLength = 2;
  autoCompleteControl: UntypedFormControl = new UntypedFormControl();
  searchedItems$: BehaviorSubject<ExtendendItem[]> = new BehaviorSubject([]);

  constructor(
    private fuseProggressBarService: FuseProgressBarService
  ) {
    this._initialValue$ = new BehaviorSubject<T>(null);
    this.loading$ = new Subject<boolean>();
  }

  ngOnInit() {
    if (!this.suggestService) {
      console.error('Cannot initialize suggest input. Service not set')
      return;
    }

    this.loading$.pipe(
      distinctUntilChanged(),
    ).subscribe({
      next: loading => {
        if (loading) {
          this.fuseProggressBarService.show();
        }
        else
          this.fuseProggressBarService.hide();
      }
    });

    this._initialValue$.pipe(
      distinctUntilChanged()
    ).subscribe({
      next: value => {
        if (value) {
          value['descriptionPropertyName'] = this.descriptionPropertyName;
          this.autoCompleteControl.setValue(value, { onlySelf: true, emitEvent: false });
          this.selectedOptionChanged(value, false);
        }
      }
    });

    if (this.formGroup.get(this.controlName).disabled) {
      this.autoCompleteControl.disable();
    }

    this.formGroup.get(this.controlName).statusChanges.subscribe(x => {
      if (x == 'DISABLED') {
        this.autoCompleteControl.disable();
      }
    });

    this.formGroup.get(this.controlName).valueChanges.subscribe({
      next: newVal => {
        if (!newVal) {
          this.autoCompleteControl.reset();
        }
      }
    })
  }

  ngAfterViewInit(): void {
    this.autoCompleteControl.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      // delay emits
      debounceTime(500),
      switchMap(value => {
        if (value !== '' && typeof value == 'string') {
          this.formGroup.get(this.controlName).reset(null, { emitEvent: false });
          return this.search(value);
        }
        else return observableOf([]);
      })
    ).subscribe({
      next: x => {
        this.searchedItems$.next(x);
      }
    })
    this.searchedItems$.subscribe({
      next: () => this.loading$.next(false)
    });
  }

  search(description: string): Observable<Item[]> {
    if (description !== null && description.length > this.searchPatternMinLength) {
      this.loading$.next(true);
      return this.suggestService.getSuggestByDescription(description).pipe(
        map(items => {
          return items.map(x => {
            x['descriptionPropertyName'] = this.descriptionPropertyName;
            return x;
          });
        })
      )
    }
    else {
      return observableOf(null);
    }
  }

  displayItem(item?: ExtendendItem): string | undefined {
    return item ? item[item.descriptionPropertyName] : undefined;
  }

  selectedOptionChanged(item: T, emitItem: boolean = true) {
    this.formGroup.get(this.controlName).patchValue(item.id);

    if (emitItem) {
      this.itemChange.emit(item);
    }
  }
}
